import { MantineColorsTuple } from '@mantine/core';

// Type-safe color scale
type ColorScale = {
	readonly [K in 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9]: string;
};

// Brand colors from the design guide
export const brandColors = {
	primary: '#1A1A1A',
	neutral: {
		0: '#FFFFFF',
		1: '#F5F5F5',
		2: '#E6E6E6',
		3: '#CCCCCC',
		4: '#B3B3B3',
		5: '#808080',
		6: '#666666',
		7: '#333333',
		8: '#1A1A1A',
		9: '#050505',
	} as ColorScale,
	info: {
		0: '#F2F4FA',
		1: '#E6E9F5',
		2: '#D9DEF0',
		3: '#BFCCEB',
		4: '#A6B9E6',
		5: '#668CCF',
		6: '#4C79C9',
		7: '#264FA6',
		8: '#193880',
		9: '#000D33',
	} as ColorScale,
	success: {
		0: '#F5FAF2',
		1: '#EBF5E6',
		2: '#E0F0D9',
		3: '#CCEBBD',
		4: '#B8E6A1',
		5: '#8FD966',
		6: '#7ACC4D',
		7: '#4DA626',
		8: '#39801A',
		9: '#113300',
	} as ColorScale,
	error: {
		0: '#fff5f5',
		1: '#ffe3e3',
		2: '#ffc9c9',
		3: '#ffa8a8',
		4: '#ff8787',
		5: '#ff6b6b',
		6: '#fa5252',
		7: '#f03e3e',
		8: '#e03131',
		9: '#c92a2a',
	} as ColorScale,
	warning: {
		0: '#FAF8F5',
		1: '#F5F0EB',
		2: '#F0E9E0',
		3: '#EBDACC',
		4: '#E6CBB8',
		5: '#CFAB8F',
		6: '#C9987A',
		7: '#A66F4D',
		8: '#805239',
		9: '#523600',
	} as ColorScale,
} as const;

// Helper to convert color scale to Mantine tuple
function colorScaleToTuple(scale: ColorScale | string): MantineColorsTuple {
	if (typeof scale === 'string') {
		// For single color like primary, create a tuple with the same color
		return [scale, scale, scale, scale, scale, scale, scale, scale, scale, scale];
	}
	// For color scales, convert to array
	return [
		scale[0],
		scale[1],
		scale[2],
		scale[3],
		scale[4],
		scale[5],
		scale[6],
		scale[7],
		scale[8],
		scale[9],
	];
}

// Export color scales for Mantine
export const primaryColorScale = colorScaleToTuple(brandColors.primary);
export const neutralColorScale = colorScaleToTuple(brandColors.neutral);
export const infoColorScale = colorScaleToTuple(brandColors.info);
export const successColorScale = colorScaleToTuple(brandColors.success);
export const errorColorScale = colorScaleToTuple(brandColors.error);
export const warningColorScale = colorScaleToTuple(brandColors.warning);

// Type for semantic colors to ensure light and dark have same structure
type SemanticColorMap = {
	// Backgrounds
	background: string;
	surface: string;

	// Text
	text: string;
	textSecondary: string;
	textMuted: string;
	textOnPrimary: string;

	// Borders
	border: string;
	borderLight: string;

	// Interactive
	hover: string;
	active: string;
	focus: string;

	// Status
	info: string;
	success: string;
	warning: string;
	error: string;

	// Status backgrounds
	infoBg: string;
	successBg: string;
	warningBg: string;
	errorBg: string;
};

// Semantic color mapping for light mode
export const lightColors: SemanticColorMap = {
	// Backgrounds
	background: '#F6F4F1',
	surface: '#FCFBFA',

	// Text
	text: brandColors.neutral[9],
	textSecondary: brandColors.neutral[7],
	textMuted: brandColors.neutral[5],
	textOnPrimary: brandColors.neutral[0],

	// Borders
	border: brandColors.neutral[3],
	borderLight: brandColors.neutral[2],

	// Interactive
	hover: brandColors.neutral[2],
	active: brandColors.neutral[3],
	focus: brandColors.primary,

	// Status
	info: brandColors.info[6],
	success: brandColors.success[6],
	warning: brandColors.warning[6],
	error: brandColors.error[6],

	// Status backgrounds
	infoBg: brandColors.info[0],
	successBg: brandColors.success[0],
	warningBg: brandColors.warning[0],
	errorBg: brandColors.error[0],
};

// Semantic color mapping for dark mode
export const darkColors: SemanticColorMap = {
	// Backgrounds
	background: brandColors.neutral[8],
	surface: brandColors.neutral[9],

	// Text
	text: brandColors.neutral[0],
	textSecondary: brandColors.neutral[2],
	textMuted: brandColors.neutral[4],
	textOnPrimary: brandColors.neutral[0],

	// Borders
	border: brandColors.neutral[7],
	borderLight: '#3A3A3A',

	// Interactive
	hover: '#2A2A2A',
	active: '#3A3A3A',
	focus: brandColors.primary,

	// Status
	info: brandColors.info[4],
	success: brandColors.success[4],
	warning: brandColors.warning[4],
	error: brandColors.error[4],

	// Status backgrounds
	infoBg: 'rgba(102, 140, 207, 0.1)',
	successBg: 'rgba(143, 217, 102, 0.1)',
	warningBg: 'rgba(207, 171, 143, 0.1)',
	errorBg: 'rgba(255, 107, 107, 0.1)',
};

// Export type for semantic colors
export type SemanticColors = SemanticColorMap;

// Helper to get semantic colors based on color scheme
export const getSemanticColors = (colorScheme: 'light' | 'dark'): SemanticColorMap => {
	return colorScheme === 'dark' ? darkColors : lightColors;
};

// CSS variables for semantic colors
export const generateColorVariables = (colorScheme: 'light' | 'dark') => {
	const colors = getSemanticColors(colorScheme);
	const variables: Record<string, string> = {};

	Object.entries(colors).forEach(([key, value]) => {
		variables[`--color-${key}`] = value;
	});

	return variables;
};
